import React, { createContext, useState, useEffect, useContext } from 'react';
import { jwtDecode } from 'jwt-decode';
import Cookies from 'js-cookie';  // Importer js-cookie

// Funksjon for å sjekke om brukeren er frosset
const checkIfUserIsFrozen = async () => {
  const apiUrl = process.env.REACT_APP_STATUS_API_URL;
  const token = Cookies.get('token');  // Hent JWT-token fra cookies

  if (!token) {
    console.error('No token found.');
    return false;
  }

  try {
    const decodedToken = jwtDecode(token);
    const username = decodedToken.sub;  // Hent 'sub' fra tokenet som brukernavn

    const response = await fetch(`${apiUrl}/check_freeze`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,  // Send JWT-token i headeren
      },
      body: JSON.stringify({ username }),  // Send brukernavn i body
    });

    if (response.ok) {
      const data = await response.json();
      return data.frozen === 'true';  // Returner true hvis brukeren er frosset
    } else {
      const errorText = await response.text();
      console.error('Somthing went wrong with the response from the server:', response.status, errorText);
      return false;
    }
  } catch (error) {
    console.error('Error while checking freeze status:', error);
    return false;
  }
};

// Opprett en AuthContext
export const AuthContext = createContext();

// Create a hook that lets components access the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  // Add a getToken helper function
  const getToken = () => Cookies.get('token');
  
  // Add a user object (extracted from token if available)
  let user = null;
  const token = getToken();
  if (token) {
    try {
      const decoded = jwtDecode(token);
      user = {
        username: decoded.sub,
        // Add any other user properties from the token
      };
    } catch (error) {
      console.error('Error decoding token:', error);
    }
  }

  return {
    ...context,
    getToken,
    user
  };
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [role, setRole] = useState(null);
  const [brands, setBrands] = useState([]);  // Legg til brands som en state
  const [functions, setFunctions] = useState([]); // Ny state for funksjoner

  // Funksjon for å håndtere innlogging
  const login = async (username, password) => {
    try {
      const apiUrl = process.env.REACT_APP_STATUS_API_URL;

      const response = await fetch(`${apiUrl}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      if (response.ok) {
        const data = await response.json();

        // Hvis serveren svarer med en spesifikk feilmelding, sjekk for det
        if (data.error === 'Account is frozen') {
          console.warn('Login cancelled: Account is frozen by admin.');
          return { success: false, message: 'Account is frozen by admin' };
        }

        const decodedToken = jwtDecode(data.access_token); // Dekod token for å hente utløpstid
        const expirationDate = new Date(decodedToken.exp * 1000); // Konverter til millisekunder

        const isProduction = process.env.REACT_APP_STATUS_API_URL === 'https://scripthub.serviceorderhub.com/dashboardapi';

        Cookies.set('token', data.access_token, { 
          expires: expirationDate, 
          secure: isProduction,  // Kun sikre cookies i produksjon
          sameSite: 'Strict'     // Ekstra sikkerhet for å forhindre cross-site requests
        });

        Cookies.set('role', data.role, { 
          expires: expirationDate, 
          secure: isProduction,  // Kun sikre cookies i produksjon
          sameSite: 'Strict'
        });

        Cookies.set('brands', JSON.stringify(data.brands), { 
          expires: expirationDate, 
          secure: isProduction,  // Kun sikre cookies i produksjon
          sameSite: 'Strict'
        });

        // Hent funksjoner for brukeren og lagre i cookie
        try {
          const functionsResponse = await fetch(`${apiUrl}/user/functions`, {
            headers: { 'Authorization': `Bearer ${data.access_token}` }
          });
          if (functionsResponse.ok) {
            const functionsData = await functionsResponse.json();
            Cookies.set('functions', JSON.stringify(functionsData.functions || []), {
              expires: expirationDate,
              secure: isProduction,
              sameSite: 'Strict'
            });
          }
        } catch (err) {
          // Ikke kritisk feil, bare logg
          console.warn('Kunne ikke hente eller lagre functions-cookie:', err);
        }

        // Force small sidebar for certain roles - improve to apply immediately
        if (data.role === 'repair-cost' || data.role === 'price-admin') {
          console.log(`Setting small sidebar for role: ${data.role}`);
          localStorage.setItem('useSmallSidebar', 'true');
        }

        // Hent brukerdata fra API-et
        const userResponse = await fetch(`${apiUrl}/user?username=${username}&token=${data.access_token}`, {
          headers: {
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache'
          }
        });
        const userData = await userResponse.json();

        // Konverter functions-verdien til et array hvis det er en streng
        let userFunctions = [];
        if (userData.functions && typeof userData.functions === "string") {
          userFunctions = userData.functions.split(',').map(func => func.trim());
        }

        // Set states in correct order to trigger our useRoleSpecificSettings hook
        setBrands(data.brands || []);
        setFunctions(userFunctions);
        setRole(data.role);  // This will trigger the useRoleSpecificSettings hook
        setIsAuthenticated(true);
        
        console.log("User functions set:", userFunctions); // Debug logging
        
        return { success: true };
      } else {
        const errorData = await response.json();
        console.error('Login failed:', errorData.error);
        return { success: false, message: errorData.error || 'Wrong username or password.' };
      }
    } catch (error) {
      console.error('Login failed:', error);
      return { success: false, message: 'Could not login. Please try again later.' };
    }
  };

  // Funksjon for å håndtere utlogging
  const logout = async () => {
    // Mark user as offline before removing token
    try {
      const token = Cookies.get('token');
      const apiUrl = process.env.REACT_APP_API_URL || '';
      
      if (token) {
        // Make API call to set user as offline
        await fetch(`${apiUrl}/user/update_status`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ is_online: false })
        });
        
        console.log('User marked as offline on logout');
      }
    } catch (error) {
      console.error('Failed to update online status during logout:', error);
    }
    
    // Continue with normal logout procedure
    Cookies.remove('token');  // Fjern token fra cookies
    Cookies.remove('role');   // Fjern rollen fra cookies
    Cookies.remove('brands'); // Fjern brands fra cookies
    Cookies.remove('functions'); // Fjern functions fra cookies
    setIsAuthenticated(false);  // Nullstill autentiseringstilstanden
    setRole(null);  // Nullstill rollen
    setBrands([]);  // Nullstill brands
    setFunctions([]); // Nullstill functions ved utlogging
  };

  // Funksjon for å sjekke om token er utløpt
  const isTokenExpired = (token) => {
    try {
      const decodedToken = jwtDecode(token);
      const currentTime = Date.now() / 1000;  // Nåværende tid i sekunder
      return decodedToken.exp < currentTime;  // Returner true hvis tokenet er utløpt
    } catch (error) {
      console.error('Error while decoding the token.:', error);
      return true;  // Hvis det er feil, anta at tokenet er utløpt
    }
  };

  // Sjekk token, role og brands ved første innlasting av appen
  useEffect(() => {
    const checkLoginStatus = async () => {
      // Skip token check if we're on /esd-check
      if (window.location.pathname === '/esd-check') {
        return;
      }

      const token = Cookies.get('token');
      if (!token) {
        setIsAuthenticated(false);
        setRole(null);
      } else {
        const role = Cookies.get('role');  // Hent role fra cookies
        const brandsFromCookie = Cookies.get('brands');  // Hent brands fra cookies
        const functionsFromCookie = Cookies.get('functions'); // Hent functions fra cookies

        let userFunctions = [];
        if (functionsFromCookie) {
          try {
            userFunctions = JSON.parse(functionsFromCookie);
          } catch (e) {
            userFunctions = [];
          }
        }

        if (token && role && brandsFromCookie) {
          // Force small sidebar for certain roles - add this here as well
          if (role === 'repair-cost' || role === 'price-admin') {
            console.log(`Setting small sidebar for role: ${role} during app startup`);
            localStorage.setItem('useSmallSidebar', 'true');
          }

          if (isTokenExpired(token)) {
            console.log('The token has expired');
            logout();  // Logg ut hvis tokenet er utløpt
          } else {
            const isFrozen = await checkIfUserIsFrozen();  // Sjekk om brukeren er frosset
            if (isFrozen) {
              console.log('Use account has been frozen');
              logout();
            } else {
              setIsAuthenticated(true);
              setRole(role);
              setBrands(JSON.parse(brandsFromCookie));
              setFunctions(userFunctions); // Sett functions fra cookie
              console.log('User functions loaded from cookie:', userFunctions);
            }
          }
        } else {
          logout();  // Hvis token, role eller brands mangler, logg ut brukeren
        }
      }
    };

    checkLoginStatus();
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, role, brands, functions, login, logout, setIsAuthenticated, setRole, setBrands, setFunctions }}>
      {children}
    </AuthContext.Provider>
  );
};
